import { ClientError } from 'graphql-request';
import * as React from 'react';

import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  GetProductsQuery,
  StripePrice,
  User,
  useGetProductsQuery,
} from 'src/ui/graph/types';

export const useStripePrices = () => {
  const [stripePrices, setStripePrices] = React.useState<StripePrice[]>();
  const [profile, setProfile] = React.useState<
    Omit<User, 'is_authenticated' | 'username'>
  >();
  const { data, isLoading, error } = useGetProductsQuery(
    graphQLClient,
    undefined,
    {
      // A failed query has no cached data, so refetching (on tab focus or
      // remount) drops the page back to the loading spinner. Products/pricing
      // don't change often enough to need those refetches.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  // graphql-request throws a ClientError on a partial-success response but
  // still carries the resolved fields (profile/queue) in `response.data`.
  const partialData =
    error instanceof ClientError
      ? (error.response.data as Partial<GetProductsQuery> | undefined)
      : undefined;
  const payload = data ?? partialData;

  // Prices are unavailable once loading finishes without an array of prices —
  // a partial failure (getStripePrices came back null), a total failure (no
  // payload at all), or any other error. An empty array still counts as
  // available.
  const pricesUnavailable = !isLoading && !payload?.getStripePrices;

  React.useEffect(() => {
    if (!payload?.profile) {
      return;
    }
    setStripePrices(
      (payload.getStripePrices ?? []).flatMap((price) => {
        if (!price) {
          return [];
        }

        const stripePrice: StripePrice = {
          id: price.id,
          nickname: price.nickname,
          amount: price.amount,
          stardog_billing_type: price.stardog_billing_type,
          product_info: {
            name: price.product_info?.name,
            description: price.product_info?.description,
            metadata: {
              flavor: price.product_info?.metadata?.flavor,
            },
          },
        };
        return [stripePrice];
      })
    );
    setProfile(payload.profile);
  }, [payload]);

  return {
    isLoading,
    pricesUnavailable,
    profile,
    stripePrices,
  };
};
